/**
 * spendable.js — Spendable balance algorithm
 *
 * The core metric of the Culp Financial Planner.
 *
 * Formula:
 *   spendable = checking_balance - sum(unpaid bills due before next payday)
 *   daily_allowance = spendable / days_until_payday
 *
 * This tells you: "After covering all upcoming bills, how much can you
 * actually spend between now and your next paycheck?"
 *
 * If negative: "You are $X short for upcoming bills — danger zone."
 */

import { isBillDueBeforePayday, getDaysUntilPayday } from './dates';

/**
 * Calculate the spendable balance.
 *
 * @param {Object} params
 * @param {number} params.checkingBalance - Current checking account balance
 * @param {Array} params.bills - Array of bill objects from Firestore
 * @param {number} params.bills[].amount - Bill amount in dollars
 * @param {number} params.bills[].due_day - Day of month (1-31)
 * @param {boolean} params.bills[].current_cycle_paid - Whether already paid this cycle
 * @param {boolean} params.bills[].active - Whether bill is active
 * @param {string} params.paydayAnchor - ISO date string of a known payday
 * @param {Date} [params.today] - Reference date for testing
 * @returns {Object} Spendable calculation result
 */
export function calculateSpendable({ checkingBalance, bills, paydayAnchor, today = new Date() }) {
  // Filter to unpaid, active bills due before next payday
  const upcomingBills = bills.filter(bill =>
    bill.active !== false &&
    !bill.current_cycle_paid &&
    isBillDueBeforePayday(bill.due_day, paydayAnchor, today)
  );

  const upcomingTotal = upcomingBills.reduce((sum, bill) => sum + (bill.amount || 0), 0);
  const spendable = checkingBalance - upcomingTotal;
  const daysUntilPayday = getDaysUntilPayday(paydayAnchor, today);
  const dailyAllowance = daysUntilPayday > 0 ? spendable / daysUntilPayday : 0;

  return {
    /** Net spendable after upcoming bills */
    spendable,
    /** Dollars per day until next payday */
    dailyAllowance,
    /** Days until next payday */
    daysUntilPayday,
    /** Total of upcoming unpaid bills */
    upcomingBillsTotal: upcomingTotal,
    /** Number of upcoming unpaid bills */
    upcomingBillsCount: upcomingBills.length,
    /** The upcoming bills themselves (for breakdown display) */
    upcomingBills,
    /** Current checking balance (passthrough for display) */
    checkingBalance,
    /** Whether the user is short on funds */
    isNegative: spendable < 0,
    /** Whether daily allowance is dangerously low (< $20/day) */
    isTight: dailyAllowance > 0 && dailyAllowance < 20,
  };
}

/**
 * Get a human-readable status message for the spendable balance.
 * @param {Object} result - Output from calculateSpendable()
 * @returns {string} Status message
 */
export function getSpendableStatus(result) {
  if (result.isNegative) {
    return `You're $${Math.abs(result.spendable).toFixed(0)} short for upcoming bills`;
  }
  if (result.isTight) {
    return `Tight — $${result.dailyAllowance.toFixed(0)}/day until payday`;
  }
  if (result.dailyAllowance >= 100) {
    return `You're in good shape`;
  }
  return `$${result.dailyAllowance.toFixed(0)}/day until payday`;
}
